import { Booking, BookingStage, BookingStatus } from '../data/bookings';
import { AddressLabel, formatAddress, SavedAddress } from '../data/places';
import { CategoryKey, Service } from '../data/services';
import { api } from './client';
import { ApiError } from './types';

/**
 * Bookings, from the platform.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS
 * ---------------------------------------------------------------------------
 * The app has been booking into its own memory. A checkout minted a reference
 * like `#HOM17934` on the phone, kept the booking in a React state array, and
 * lost it on the next launch - while the platform knew nothing about any of
 * it. Support could not look one up, dispatch could not assign anybody, and a
 * professional assigned in the database was invisible on the device.
 *
 * From here a booking is a row on the server with a number the platform
 * minted, and this module is the only thing that turns one into the shape the
 * screens already draw.
 *
 * ---------------------------------------------------------------------------
 * TWO VOCABULARIES
 * ---------------------------------------------------------------------------
 * The server has nine statuses; the app's detail screen has five stages and
 * four filters. They are not the same axis and the mapping is not one-to-one
 * - `arrived` is a stage the app draws inside "on the way", and `assigning`
 * is something it calls "booked". Every one of those decisions is written down
 * in `stageOf` and `statusOf` rather than spread across the screens.
 */

/** One booking, as `GET /bookings` returns it. */
type ApiBooking = {
  id: string;
  reference: string;
  status: string;
  title: string;
  serviceId: string;
  /** The customer's own saved address this was booked against - required to
      recreate a booking, which is how `useBookings.reschedule` moves one. */
  addressId: string;
  durationMinutes: number | null;
  /** Hours of a Pro this bought. Null on a fixed-price service. */
  hours?: number | null;
  /** Minutes added while the job ran. Zero on everything else. */
  extensionMinutes?: number;
  /** How much more time this visit may run. Zero when it cannot be extended. */
  extendableMinutes?: number;
  address: string;
  price: number;
  paymentStatus: string;
  /** `'cash' | 'online'` - frozen at creation. Required to recreate a
      booking the same way it was originally paid for. */
  paymentMode: string;
  bookingType: string;
  slotStartAt: string | null;
  slotEndAt: string | null;
  createdAt: string;
  completedAt: string | null;
  cancelledAt: string | null;
  cancelledByType: string | null;
  cancellationFeeAmount: number | null;
  refundedAmount: number | null;
  /**
   * Coins spent on this booking. Null when none were, which is most of them.
   *
   * Stays at what was SPENT after a cancellation - it is a record of the
   * original redemption, not a live balance. What came back is
   * `coinsRefunded` below, and the two are shown together so a partial
   * reversal (a fee kept in coins, say) cannot be mistaken for a full one.
   */
  coinsRedeemed: number | null;
  /**
   * Coins put back by a cancellation. Null until one happens.
   *
   * Deliberately separate from `coinsRedeemed` rather than zeroing it: a
   * booking that spent 200 and got 200 back is a different thing from a
   * booking that never spent any, and the statement has a movement for the
   * first and nothing for the second.
   */
  coinsRefunded: number | null;
  invoiceNumber: string | null;
  taxAmount: number | null;
  /** Time added while the job ran, paid for separately. Null when none. */
  extensionAmount?: number | null;
  /**
   * The code to read out so the professional can start.
   *
   * Sent ONLY while they are at the door - `arrived` and nothing else. Before
   * that there is nothing to read out; after `started` it is spent. The app
   * shows it or it does not; it never holds one for later.
   */
  startOtp: string | null;
  /**
   * The END code - the same handshake at the other end of the job.
   *
   * Sent ONLY while the work is running (`started`): minted the moment the
   * start code is verified, spent when the Pro enters it to complete. Older
   * backends do not send the field at all, which the `?? undefined` mapping
   * below treats the same as "no code" - the panel simply does not draw.
   */
  endOtp?: string | null;
  professional: {
    name: string | null;
    rating: number | null;
    /**
     * A signed GET for the professional's photo, or null when they have none.
     *
     * OPTIONAL ON THE TYPE because the backend does not send it yet - the
     * field is declared here so the app is ready for it, and every read below
     * treats "absent" and "null" the same way: fall back to the initials
     * avatar. Nothing renders a broken image while the server is silent.
     *
     * Expected to be short-lived and signed, like `profilePhoto.ts`'s view
     * URLs, so it must never be cached or persisted - read it fresh from the
     * booking each time.
     */
    photoUrl?: string | null;
  } | null;
  /**
   * How many minutes until the professional arrives, as the server computes it.
   *
   * ---------------------------------------------------------------------------
   * WHY A NUMBER AND NOT A SENTENCE
   * ---------------------------------------------------------------------------
   * The app used to show `"18 mins"` - a hardcoded string in seed data, with
   * `eta` mapped to `undefined` for every real booking. So the one number a
   * customer waiting at home actually wants was fiction on the examples and
   * missing on everything else.
   *
   * Sent as minutes rather than as prose so the app can format it ("18 mins",
   * "1 hr 5 mins"), translate it, and decide when it is too stale to show.
   * A server sending "18 mins" would pin the app to English and to whatever
   * rounding the backend happened to pick.
   *
   * OPTIONAL, and absent is the normal case today: the field does not exist on
   * the server yet, and it should only ever be sent while the professional is
   * actually travelling. No value means the panel says they are on the way
   * without inventing a time - see `BookingDetailScreen`.
   */
  etaMinutes?: number | null;
  review: {
    rating: number;
    comment: string | null;
    tags: string[];
    photoUrls: string[];
  } | null;
};

type BookingEvent = { status: string; by: string; at: string };

type ApiBookingDetail = ApiBooking & {
  timeline: BookingEvent[];
};

/**
 * When the professional actually started, as an ISO instant.
 *
 * The booking payload has no `startedAt` column - the moment lives in the
 * timeline, as the `at` of the `started` transition. Only the DETAIL endpoint
 * sends a timeline, which is the right place for it: the list draws cards, and
 * a card has no running clock to feed.
 *
 * The LAST matching entry, not the first. A job that was started, paused and
 * started again should count from the most recent start; taking the first
 * would tell somebody their fifteen-minute job is two hours in.
 *
 * Undefined when there is no such entry, which the clock reads as "no reading
 * yet" rather than as zero - see `jobProgress`.
 */
const startedAtOf = (row: ApiBooking): string | undefined => {
  const timeline = (row as Partial<ApiBookingDetail>).timeline;
  if (!Array.isArray(timeline)) {
    return undefined;
  }

  let latest: string | undefined;
  for (const event of timeline) {
    if (event?.status === 'started' && event.at) {
      latest = event.at;
    }
  }
  return latest;
};

/**
 * One address on file, as `GET /customers/me/addresses` returns it.
 *
 * The pin is `pinLat`/`pinLng`, not `lat`/`lng` - the server's own names, and
 * worth spelling out because the body `POST` takes uses the same two, while
 * `SavedAddress` in `data/places` calls them `coords.lat` / `coords.lng`.
 */
export type SavedAddressRow = {
  id: string;
  label: ServerAddressLabel;
  addressLine: string;
  landmark: string | null;
  pinLat: number;
  pinLng: number;
  /** Resolved by the server from the pin. The booking's serviceability gate. */
  cityId: string;
  isDefault: boolean;
};

/** What the server files an address under. The app's own labels differ. */
export type ServerAddressLabel = 'home' | 'office' | 'other';

/**
 * Where a booking is, in the app's five steps.
 *
 * `arrived` deliberately maps to `onway` rather than gaining a sixth step:
 * arriving is the END of being on the way, not a stage of its own, and the
 * detail screen already distinguishes the two by whether there is a start code
 * to show. `assigning` is "booked" because from the customer's side nothing
 * has happened yet - somebody is being looked for.
 */
const stageOf = (status: string): BookingStage => {
  switch (status) {
    case 'assigned':
      return 'assigned';
    case 'en_route':
    case 'arrived':
      return 'onway';
    case 'started':
      return 'service';
    case 'completed':
      return 'completed';
    default:
      /* created, awaiting_payment, assigning - and anything the server grows
         later, which is safer as "booked" than as a crash. */
      return 'booked';
  }
};

/** Which filter it lands under. Four shelves, nine statuses. */
const statusOf = (row: ApiBooking): BookingStatus => {
  if (row.status === 'cancelled') {
    return 'cancelled';
  }
  if (row.status === 'completed') {
    return 'completed';
  }
  return row.bookingType === 'recurring' ? 'recurring' : 'live';
};

/** "Today, 10:00 AM" / "21 May, 9:00 AM" - the app shows dates, not timestamps. */
const when = (iso: string | null): string => {
  if (!iso) {
    return '';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const time = date.toLocaleTimeString('en-IN', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  const today = new Date();
  const sameDay =
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();

  if (sameDay) {
    return `Today, ${time}`;
  }
  return `${date.getDate()} ${date.toLocaleString('en-IN', {
    month: 'short',
  })}, ${time}`;
};

/** "2 hrs", from the service's own estimate. */
const duration = (minutes: number | null): string => {
  if (!minutes) {
    return '';
  }
  if (minutes < 60) {
    return `${minutes} mins`;
  }
  const hours = minutes / 60;
  return `${Number.isInteger(hours) ? hours : hours.toFixed(1)} hrs`;
};

/**
 * "18 mins" / "1 hr 5 mins" - the arrival estimate, from the server's minutes.
 *
 * ---------------------------------------------------------------------------
 * UNDEFINED IS AN ANSWER, AND IT IS THE COMMON ONE
 * ---------------------------------------------------------------------------
 * Returns undefined for null, for a missing field, and for anything that is
 * not a finite number - and the panel that reads this draws "on the way" with
 * no figure when it gets one. That is the honest outcome: the alternative is
 * the app inventing a time, which is the bug this replaces.
 *
 * Zero and negatives collapse to "Arriving now" rather than "0 mins" or a
 * minus sign. A late professional is the case most likely to produce a
 * negative here, and it is exactly the case where a nonsense figure would be
 * read most carefully.
 */
const etaText = (minutes: number | null | undefined): string | undefined => {
  if (minutes == null || !Number.isFinite(minutes)) {
    return undefined;
  }
  const whole = Math.round(minutes);
  if (whole <= 0) {
    return 'Arriving now';
  }
  if (whole < 60) {
    return `${whole} ${whole === 1 ? 'min' : 'mins'}`;
  }

  const hours = Math.floor(whole / 60);
  const rest = whole % 60;
  const hourPart = `${hours} ${hours === 1 ? 'hr' : 'hrs'}`;
  return rest ? `${hourPart} ${rest} mins` : hourPart;
};

/**
 * One line on what is happening, for the card and the live strip.
 *
 * Written from the status rather than sent by the server, because it is copy
 * and copy belongs with the screens that show it. What the server sends is the
 * fact; this is how the app says it.
 */
const progressOf = (row: ApiBooking): string | undefined => {
  switch (row.status) {
    /*
     * Not "Finding a professional". Nobody is looking: an unpaid online
     * booking never reaches dispatch, and it is released after the hold
     * window. The old copy promised activity that was not happening, on a
     * booking the customer had walked away from.
     */
    case 'awaiting_payment':
      return 'Not confirmed yet - payment pending';
    case 'created':
    case 'assigning':
      return 'Finding a professional';
    case 'assigned':
      return 'Professional assigned';
    case 'en_route':
      return 'Professional is on the way';
    case 'arrived':
      return 'Professional has arrived';
    case 'started':
      return 'Service in progress';
    case 'cancelled':
      return row.cancelledByType && row.cancelledByType !== 'customer'
        ? 'Cancelled by Homingo. You have been refunded in full.'
        : 'You cancelled this booking.';
    default:
      return undefined;
  }
};

/**
 * A server booking, in the shape every screen already draws.
 *
 * `services` is the live catalogue, used for the two things the bookings
 * endpoint does not carry: which category the service belongs to (the app
 * colours and illustrates by trade) and the tags on the card.
 */
export const toAppBooking = (
  row: ApiBooking,
  services: Service[],
  categoryLabel: (key: CategoryKey) => string,
): Booking => {
  const service = services.find(item => item.key === row.serviceId);
  const category = service?.category ?? 'cleaning';
  const label = categoryLabel(category);

  const professional = row.professional?.name
    ? {
        name: row.professional.name,
        rating: row.professional.rating ?? 0,
        /* The endpoint does not send these and the app must not invent them -
           a jobs count and a job title are claims about a real person. */
        jobs: 0,
        title: label,
        verified: true,
        /* Absent and null both mean "no picture" - the card falls back to
           initials rather than rendering a broken image. */
        photoUrl: row.professional.photoUrl ?? undefined,
      }
    : undefined;

  return {
    id: row.id,
    reference: row.reference,
    title: row.title,
    /* The link "Book again" follows. Names differ between the two
       catalogues; ids do not. */
    serviceId: row.serviceId,
    /* Both feed a reschedule, which recreates the booking rather than
       moving it - see `useBookings.reschedule`. */
    addressId: row.addressId,
    paymentMode: row.paymentMode as PaymentMode,
    awaitingPayment: row.status === 'awaiting_payment',
    category,
    status: statusOf(row),
    stage: stageOf(row.status),
    when: when(row.slotStartAt ?? row.createdAt),
    /*
     * The same instant `when` was formatted from, kept unformatted.
     *
     * `when` is prose and nothing can be measured against it. The free
     * cancellation window is six hours before the VISIT, so it needs the
     * scheduled start specifically - never `createdAt`, which is when the
     * booking was made and would put every instant job permanently inside
     * the late window.
     */
    slotStartAt: row.slotStartAt ?? undefined,
    duration: duration(row.durationMinutes),
    /* Both feed the "stay another hour" card, which only a service sold by
       the hour can offer at all. */
    hours: row.hours ?? null,
    extensionMinutes: row.extensionMinutes ?? 0,
    /* The server's own answer, because the cap belongs to the service and
       ops moves it. */
    extendableMinutes: row.extendableMinutes ?? 0,
    slotEndAt: row.slotEndAt ?? undefined,
    address: row.address,
    price: row.price,
    payment: {
      paid: row.paymentStatus === 'paid' ? row.price : 0,
      /*
       * Three states, not two. "Pay after service" is true of a cash
       * booking and false of an unpaid online one - that one is waiting for
       * money now, and telling the customer to pay later is how they end up
       * watching it expire.
       */
      method:
        row.paymentStatus === 'paid'
          ? 'Paid online'
          : row.status === 'awaiting_payment'
          ? 'Payment pending'
          : 'Pay after service',
    },
    services: [row.title],
    tags: [label],
    professional,
    progress: progressOf(row),
    /*
     * Read off the timeline rather than a column of its own - see
     * `startedAtOf`. This was hardcoded `undefined`, which meant the running
     * clock on a job in progress never had an instant to count from and the
     * whole timer panel rendered as nothing.
     */
    startedAt: startedAtOf(row),
    /* Straight through: the Home bar uses it to stop asking for a rating on
       something that finished days ago. */
    completedAt: row.completedAt ?? undefined,
    /*
     * Present only on `arrived`, because that is the only time the server
     * sends one - and its presence is what switches the detail screen from the
     * "on the way" panel to the arrival panel with the code. The screen needs
     * no change; it has always keyed off this.
     */
    otp: row.startOtp ?? undefined,
    /* The closing half of the same handshake - shown while work is running. */
    endOtp: row.endOtp ?? undefined,
    /*
     * The arrival estimate, formatted from the server's minutes.
     *
     * This was hardcoded `undefined`, so no real booking ever showed one and
     * the only ETAs in the app were the strings written into the seed data by
     * hand. Now it is whatever the server says, and nothing when the server
     * says nothing - see `etaText` and `ApiBooking.etaMinutes`.
     */
    eta: etaText(row.etaMinutes),
    refundedAmount: row.refundedAmount ?? undefined,
    /* For the wallet's merged activity list - see `Booking.cancelledAt`. */
    cancelledAt: row.cancelledAt ?? undefined,
    cancellationFeeAmount: row.cancellationFeeAmount ?? undefined,
    coinsRedeemed: row.coinsRedeemed ?? undefined,
    coinsRefunded: row.coinsRefunded ?? undefined,
    invoiceNumber: row.invoiceNumber ?? undefined,
    taxAmount: row.taxAmount ?? undefined,
    extensionAmount: row.extensionAmount ?? undefined,
    review: row.review
      ? {
          stars: row.review.rating,
          comment: row.review.comment,
          tags: row.review.tags,
          photoUrls: row.review.photoUrls,
        }
      : undefined,
  };
};

// ------------------------------------------------------------------ the calls

export const fetchBookings = (signal?: AbortSignal) =>
  api.get<ApiBooking[]>('/bookings', { signal });

export const fetchBooking = (id: string, signal?: AbortSignal) =>
  api.get<ApiBookingDetail>(`/bookings/${id}`, { signal });

/**
 * How the booking is settled. Frozen at creation - no route can change it
 * afterwards, which is why it is an input rather than a later update.
 */
export type PaymentMode = 'cash' | 'online';

/**
 * Book one service.
 *
 * Sends no money - the server prices it from the catalogue and freezes that
 * price, and a request carrying its own amount is rejected outright.
 *
 * `mode` is not sent and does not exist server-side: a booking is scheduled
 * when `slotStartAt` is present and instant when it is absent, and the server
 * derives the end of the slot from the service's own duration - or from
 * `hours` on a service sold by the hour.
 */
export const createBooking = (
  input: {
    serviceId: string;
    addressId: string;
    paymentMode: PaymentMode;
    slotStartAt?: string;
    /**
     * How many hours of a Pro's time to buy.
     *
     * Required on a service whose `pricingModel` is `hourly` and
     * REFUSED on a fixed one - the platform does not ignore a stray value, it
     * rejects the request, which is what stops a three-hour screen booking a
     * forty-five-minute job.
     *
     * It sets the price as well as the length: the total is the service's
     * hourly rate times this, computed and frozen server-side. Nothing here
     * sends an amount.
     */
    hours?: number;
    /**
     * Coins to spend on THIS booking, as a count - never a rupee figure.
     *
     * The server prices the coins itself, exactly as it prices the service: a
     * request naming its own discount is refused the same way a request naming
     * its own price is. Omitted entirely when none are being spent, so a
     * booking that redeemed nothing carries no redemption rather than a zero
     * the ledger would still have to record a movement for.
     */
    coinsToRedeem?: number;
  },
  /**
   * Makes a repeat of this exact call return the original booking instead of
   * making a second one. Required for anything the customer can retry.
   */
  idempotencyKey?: string,
) =>
  api.post<ApiBooking>('/bookings', input, {
    headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
  });

/**
 * Book a basket - one booking PER SERVICE, because that is the platform's
 * unit of dispatch.
 *
 * The fan-out is here rather than on the server: `POST /bookings` takes a
 * single `serviceId`, so a basket of three is three calls. Sequential, not
 * `Promise.all`, because each one is priced, serviceability-checked and
 * dispatched on its own, and firing them together turns one rejected service
 * into three ambiguous failures.
 *
 * ---------------------------------------------------------------------------
 * WHY EVERY CALL CARRIES A KEY
 * ---------------------------------------------------------------------------
 * Rejecting halfway leaves earlier bookings standing - they are real, the
 * customer can see them, and rolling them back would be a second write that
 * can fail on its own. So the retry has to be safe instead: the key is derived
 * from the order reference and the item's position, both stable across
 * attempts, so re-running returns the bookings already made and only creates
 * the ones that never landed.
 *
 * The index is part of the key because a quantity of two is the same
 * `serviceId` twice, and those are two genuinely different visits.
 *
 * ---------------------------------------------------------------------------
 * COINS ARE SPENT ONCE, ON ONE BOOKING
 * ---------------------------------------------------------------------------
 * The basket redeems a single figure, but the basket is not what gets booked -
 * N separate bookings are, and each is cancelled and refunded on its own. So
 * the coins have to land on exactly one of them, and the obvious alternatives
 * are both wrong:
 *
 *   · Passing `coinsToRedeem` to every call spends the balance N times. The
 *     cart quoted one discount and the ledger would show three.
 *   · Splitting it across the N bookings makes every later cancellation a
 *     fractional reversal, and coins are integers - a 3-way split of 100
 *     cannot be put back without inventing or destroying one.
 *
 * They go on the FIRST booking, whole. That booking is also the one the order
 * is named after (`PaymentOrder.title` is the first line), so the discount
 * appears against the visit the customer recognises the order by, and
 * cancelling it returns the coins in one movement.
 *
 * The consequence, stated plainly because it is a real limitation: cancelling
 * only the *other* bookings returns no coins, because none were spent on them.
 * That is correct per-booking accounting and it is what the server's ledger
 * will agree with. Whether cancelling every booking in an order should sweep
 * the redemption is an order-level policy question, and there is no order-level
 * entity to hang it on yet.
 */
export const createBookings = async (input: {
  /**
   * One entry per unit, each carrying its own length.
   *
   * A list of ids with a parallel list of hours would be correct only for as
   * long as nobody reordered or filtered one of them, and the failure - a
   * booking made for the wrong number of hours - reads as correct everywhere
   * afterwards. Pairing them makes that impossible rather than unlikely.
   */
  items: { serviceId: string; hours?: number }[];
  addressId: string;
  paymentMode: PaymentMode;
  slotStartAt?: string;
  /**
   * Coins for the basket as a whole. Charged to the first booking - see the
   * note above for why not spread, and what that costs.
   */
  coinsToRedeem?: number;
  /** The order's reference - stable for as long as this checkout lives. */
  idempotencyPrefix: string;
}): Promise<ApiBooking[]> => {
  const { items, idempotencyPrefix, coinsToRedeem, ...rest } = input;
  const made: ApiBooking[] = [];

  for (const [index, item] of items.entries()) {
    made.push(
      await createBooking(
        {
          ...rest,
          /*
           * The whole item, not just its id: an hourly service carries its own
           * `hours`, and pairing the two is what stops a booking being made
           * for the wrong length. See the note on `items`.
           */
          ...item,
          /* First booking only, and only when there is something to spend -
             `coinsToRedeem: 0` would be a redemption of nothing. */
          ...(index === 0 && coinsToRedeem ? { coinsToRedeem } : {}),
        },
        `${idempotencyPrefix}-${index}`,
      ),
    );
  }

  return made;
};

/** What the server calls each cadence. The app's own word differs for one. */
export type ServerFrequency = 'weekly' | 'biweekly' | 'monthly';

/** One repeating arrangement, as `POST /bookings/recurring-plans` returns it. */
export type ApiRecurringPlan = {
  id: string;
  serviceId: string;
  frequency: ServerFrequency;
  timeOfDay: string;
  isActive: boolean;
};

/**
 * Set up a repeating visit - one plan PER SERVICE, like bookings.
 *
 * A plan is not a booking. It is the instruction that produces them: the
 * server prices and creates each occurrence when it comes due, at that day's
 * catalogue rate. So nothing here returns something the bookings list can show
 * yet, and the screen that calls it must not pretend otherwise.
 */
export const createRecurringPlan = (input: {
  serviceId: string;
  addressId: string;
  /**
   * How many hours EACH occurrence buys, on a service sold by the hour.
   *
   * On the plan rather than on each occurrence, because there is nobody to ask
   * when the generator runs weeks later - the customer chose "three hours every
   * Thursday" once. Each occurrence is still priced at that day's rate.
   *
   * Omitted on a fixed service, where the platform refuses it outright.
   */
  hours?: number;
  frequency: ServerFrequency;
  daysOfWeek: number[];
  /** Wall-clock `HH:mm`, read in the city's timezone rather than UTC. */
  timeOfDay: string;
  /** `YYYY-MM-DD` - the first visit. */
  startDate: string;
}) => api.post<ApiRecurringPlan>('/bookings/recurring-plans', input);

export const createRecurringPlans = async (input: {
  /**
   * One plan per entry, each with its own length - the same pairing
   * `createBookings` uses, and for the same reason.
   *
   * A quantity of two IS two plans here, deliberately: two standing
   * arrangements for the same service is a real thing to want, and collapsing
   * them would silently halve what somebody signed up for.
   */
  items: { serviceId: string; hours?: number }[];
  addressId: string;
  frequency: ServerFrequency;
  daysOfWeek: number[];
  timeOfDay: string;
  startDate: string;
}): Promise<ApiRecurringPlan[]> => {
  const { items, ...rest } = input;
  const made: ApiRecurringPlan[] = [];

  for (const item of items) {
    made.push(await createRecurringPlan({ ...rest, ...item }));
  }

  return made;
};

export const cancelBooking = (id: string, reason?: string) =>
  api.post<ApiBookingDetail>(`/bookings/${id}/cancel`, { reason });

/**
 * What cancelling this booking right now would cost - the server's answer.
 *
 * Worked out by the same function that runs the cancellation, so it is the
 * number the customer is actually charged. The app's own estimate missed the
 * flat fee when a Pro is on the way, a plan that waives the fee, and paise.
 * Amounts are rupee strings ("74.25").
 */
export type CancellationPolicy = {
  window: 'A' | 'B' | 'C' | 'D' | 'E' | 'F';
  feeAmount: string;
  refundAmount: string;
  coinsReturned: number;
  requiresOps: boolean;
  reason: string;
};

export const fetchCancellationPolicy = (id: string, signal?: AbortSignal) =>
  api.get<CancellationPolicy>(`/bookings/${id}/cancellation-policy`, {
    signal,
  });

/**
 * Issues a fresh end-of-service code for a job that is running.
 *
 * ---------------------------------------------------------------------------
 * THE ONLY WAY OUT OF A LOCKOUT, AND IT IS ON THIS SIDE
 * ---------------------------------------------------------------------------
 * Five wrong entries and the professional's app stops being able to complete
 * the job. Nothing on their handset can mint a new one - the route is
 * authenticated as the customer who owns the booking, deliberately, because a
 * code the professional could reissue for themselves would not be a handshake
 * at all.
 *
 * So without this call a mistyped digit ends with a professional standing in
 * someone's home unable to close the job, and a support ticket - the exact
 * outcome a self-service resend exists to prevent.
 *
 * Returns nothing. The new digits arrive the ordinary way, on the next read of
 * the booking, so the caller refetches rather than trusting a response body.
 * Only valid while the booking is `started`; anything else is a 409.
 */
export const resendEndOtp = (id: string): Promise<void> =>
  api.post<void>(`/bookings/${id}/end-otp/resend`, {});

/**
 * What the server will allow, asked before a picker is even opened.
 *
 * ---------------------------------------------------------------------------
 * ASK FIRST, RATHER THAN REFUSE LAST
 * ---------------------------------------------------------------------------
 * Every rule that can refuse a move - the six-hour cutoff, the two-move
 * allowance, a booking already too far along - lives on the server. Without
 * this, a customer opens the calendar, picks a day, picks a time, confirms,
 * and only then learns none of it was possible. The route's own summary is
 * "Ask before opening a date picker, so a customer is never offered a slot
 * the next call would refuse", and that is what it is for.
 *
 * `message` is written to be shown verbatim - "Free to move. 2 changes left
 * on this booking." - so the app does not assemble that sentence out of the
 * numbers and get the plural wrong.
 */
export type RescheduleAllowance = {
  allowed: boolean;
  /** Machine-readable refusal, e.g. `INSIDE_CUTOFF`. Null when allowed. */
  refusal: string | null;
  /** Customer-facing, verbatim. */
  message: string;
  hoursUntilSlot: number | null;
  reschedulesRemaining: number;
  freeRescheduleHours: number;
  currentSlotStartAt: string | null;
  /** The soonest slot the server would accept - start the picker here. */
  earliestNewSlotAt: string;
};

export const fetchRescheduleAllowance = (id: string, signal?: AbortSignal) =>
  api.get<RescheduleAllowance>(`/bookings/${id}/reschedule`, { signal });

/**
 * Moving a booking, in one call.
 *
 * ---------------------------------------------------------------------------
 * THIS REPLACES A CANCEL-AND-REBOOK, AND THE DIFFERENCE IS NOT COSMETIC
 * ---------------------------------------------------------------------------
 * `useBookings.reschedule` cancelled the booking and created a new one, on the
 * belief - written into its own doc comment - that the backend had no
 * reschedule route. It has had one all along, mounted on the customer
 * controller beside `cancel`:
 *
 *   · **Money.** A move is free inside the allowance. A cancel inside the
 *     six-hour window charges a late fee, and the replacement was re-priced
 *     at whatever the service costs today rather than what was agreed.
 *   · **Identity.** The booking keeps its number. A rebook minted a new one,
 *     so the reference a customer had already been given stopped matching.
 *   · **Coins.** They stay spent. Cancelling refunded them and the new
 *     booking spent them again - two ledger movements for one unchanged visit.
 *   · **The allowance.** Two free moves were never counted, because nothing
 *     was ever recorded as a move.
 *   · **The cutoff.** The server refuses a move inside six hours *on purpose*
 *     - the Pro's day is already built around that address. Cancel-and-rebook
 *     went around the rule and charged a fee instead of honouring it.
 *
 * And the failure mode was the worst of it: two writes with no rollback. A
 * cancel that succeeded followed by a create that did not left the customer
 * with no booking at all - which is reachable, because the create runs the
 * serviceability gate again and a service not offered at that address fails
 * it every time.
 *
 * Returns the booking as it now stands - same id, same number, new slot.
 */
export const rescheduleBooking = (
  id: string,
  slotStartAt: string,
  reason?: string,
) =>
  api.post<ApiBookingDetail>(`/bookings/${id}/reschedule`, {
    slotStartAt,
    reason,
  });

/** One review as the server stores it. Its own shape, not a booking. */
export type ApiReview = {
  id: string;
  bookingId: string;
  /** Who wrote it. Only `customer` rows come back from this call. */
  reviewerType: 'customer' | 'pro';
  rating: number;
  comment: string | null;
  tags: string[];
  /** Short-lived presigned GETs. Empty while the content is hidden. */
  photoUrls: string[];
  /** True when moderation removed the words. The rating still counts. */
  contentHidden: boolean;
  createdAt: string;
};

/**
 * Rate the professional who did this job.
 *
 * ---------------------------------------------------------------------------
 * THE RESPONSE IS A REVIEW, NOT A BOOKING
 * ---------------------------------------------------------------------------
 * This was typed `ApiBookingDetail` and the caller fed the result straight
 * into `toAppBooking`. The server returns the review it just wrote -
 * `{ id, rating, comment, tags, photoUrls, ... }` - with no service, no
 * status, no slot and no professional on it. So a review that SUCCEEDED
 * replaced the booking in the list with a mapped husk: the card lost its
 * title, its stage and its times, and nothing said why.
 *
 * The failure was invisible in the obvious way - a rejected review showed an
 * error, and an accepted one broke the screen behind it.
 *
 * `tags` are keys from the server's own vocabulary, never free text. See
 * `REVIEW_TAGS` in `data/reviews.ts` for what happened when they were not.
 */
export const reviewBooking = (
  id: string,
  input: {
    rating: number;
    comment?: string;
    tags?: string[];
    /* Keys from `uploadReviewPhotos`, never local file URIs. */
    photoKeys?: string[];
  },
) => api.post<ApiReview>(`/bookings/${id}/review`, input);

/*
 * Mounted under the customer: `GET/POST /customers/me/addresses`.
 *
 * This used to call a root-mounted `/addresses`, and the comment here asserted
 * that was right. It was not - that path 404s on the deployed backend, which
 * is why saving an address silently did nothing: `useAddresses.save` threw,
 * `onDone` swallowed it best-effort, and the list stayed empty with no error
 * anywhere. `/customers/me/addresses` answers 401 signed out, which is what an
 * endpoint that exists looks like.
 *
 * `me` rather than an id: the controller resolves the customer from the bearer
 * token, so the path never spells out whose addresses these are.
 */
const ADDRESSES_PATH = '/customers/me/addresses';

export const fetchAddresses = (signal?: AbortSignal) =>
  api.get<SavedAddressRow[]>(ADDRESSES_PATH, { signal });

/**
 * `isDefault` is deliberately absent: the server does not accept it on create,
 * and validation rejects unknown fields rather than dropping them.
 */
export const saveAddressToServer = (input: {
  label: ServerAddressLabel;
  addressLine: string;
  landmark?: string;
  pinLat: number;
  pinLng: number;
}) => api.post<SavedAddressRow>(ADDRESSES_PATH, input);

/**
 * Remove an address from the account's book.
 *
 * The id is the SERVER's, not `place.id` - see `toSavedAddress`, which carries
 * the row id across so a screen can name the thing it wants deleted.
 */
export const deleteAddressFromServer = (id: string) =>
  api.delete<void>(`${ADDRESSES_PATH}/${id}`);

/**
 * Make one address the default. The server clears the flag on every other
 * row for this customer - there is only ever one.
 */
export const setDefaultAddressOnServer = (id: string) =>
  api.patch<SavedAddressRow>(`${ADDRESSES_PATH}/${id}/default`);

/**
 * Change an address in place.
 *
 * Every field optional: the screen sends only what it changed, which is what
 * keeps a label edit from having to re-send a pin it never touched.
 */
export const updateAddressOnServer = (
  id: string,
  input: {
    label?: ServerAddressLabel;
    addressLine?: string;
    landmark?: string;
    pinLat?: number;
    pinLng?: number;
  },
) => api.patch<SavedAddressRow>(`${ADDRESSES_PATH}/${id}`, input);

/** The app files addresses under three labels; the server uses three others. */
const SERVER_LABEL: Record<AddressLabel, ServerAddressLabel> = {
  Home: 'home',
  Work: 'office',
  Other: 'other',
};

/** The other direction - reading a row back rather than writing one. */
const APP_LABEL: Record<ServerAddressLabel, AddressLabel> = {
  home: 'Home',
  office: 'Work',
  other: 'Other',
};

/**
 * A server address row, in the shape the address screens already draw.
 *
 * `addressLine` is one string server-side; `Place.title`/`.subtitle` is a
 * display split the app makes on the way in (`formatAddress` joins them back
 * with `", "` - see `saveAddressToServer`'s caller, `ensureServerAddress`).
 * Splitting on the first comma only is what reverses that losslessly for any
 * line this app itself wrote; a line saved some other way just gets its
 * whole text as the title, which is still a truthful, readable row.
 *
 * `customLabel` has nowhere to go: the server's three labels are a closed
 * set with no free-text column behind "other", so a custom "Other" name
 * (e.g. "Mum's") does not survive a round trip yet. Saving one already
 * silently dropped it before this - `saveAddressToServer`'s input has no
 * such field either - so this does not lose anything that used to work.
 */
export const toSavedAddress = (row: SavedAddressRow): SavedAddress => {
  const commaIndex = row.addressLine.indexOf(', ');
  const title =
    commaIndex === -1 ? row.addressLine : row.addressLine.slice(0, commaIndex);
  const subtitle =
    commaIndex === -1 ? '' : row.addressLine.slice(commaIndex + 2);

  return {
    place: { id: row.id, title, subtitle },
    coords: { lat: row.pinLat, lng: row.pinLng },
    label: APP_LABEL[row.label],
    note: row.landmark ?? undefined,
    isDefault: row.isDefault,
  };
};

/*
 * Two pins are the same place within about a metre. Compared rounded rather
 * than exactly because the value has been through JSON and a Postgres numeric
 * since it was saved, and `===` on floats that survived that round trip is a
 * coin toss - which would mean a new address row on every booking.
 */
const samePin = (left: number, right: number): boolean =>
  Math.abs(left - right) < 0.00001;

/**
 * The server's id for an address the app only knows locally.
 *
 * The address book is the app's own state and has no server ids in it, but
 * `POST /bookings` will only accept an `addressId` that belongs to the
 * customer - the server reads the city and the pin off that row to decide
 * whether anyone can be sent. So the chosen address has to exist server-side
 * before anything can be booked, and this is the step that guarantees it.
 *
 * Matches an existing row by pin before creating one, so booking twice from
 * the same address does not fill the account's address book with duplicates.
 */
export const ensureServerAddress = async (
  address: SavedAddress,
): Promise<string> => {
  /*
   * The seeded addresses in `data/places` are lines of text with no pin, and
   * the server resolves the city from coordinates - there is nothing it could
   * do with one. Caught here so the customer is told which address is the
   * problem, rather than reading a validation error about `pinLat`.
   */
  if (!address.coords) {
    throw new ApiError(
      'This address has no location saved. Open it and set the pin on the map before booking.',
      0,
      [],
    );
  }

  const { lat, lng } = address.coords;
  const existing = await fetchAddresses();
  const match = existing.find(
    row => samePin(row.pinLat, lat) && samePin(row.pinLng, lng),
  );
  if (match) return match.id;

  const created = await saveAddressToServer({
    label: SERVER_LABEL[address.label],
    addressLine: formatAddress(address),
    landmark: address.note,
    pinLat: lat,
    pinLng: lng,
  });

  return created.id;
};

// ------------------------------------------------------- review photographs

type ReviewPhotoTicket = {
  /** Send this back in `photoKeys` once the PUT succeeds. */
  photoKey: string;
  /** Presigned S3 PUT. Short-lived, and the credential in its own right. */
  uploadUrl: string;
  expiresIn: number;
};

/**
 * Uploads one review photograph and returns the key to submit with the review.
 *
 * ---------------------------------------------------------------------------
 * THE COMPOSER HAD NOWHERE TO SEND THESE, AND THE SERVER HAD BEEN READY
 * ---------------------------------------------------------------------------
 * `POST /bookings/:id/review/photos/upload-url` has existed for as long as the
 * reviews module, and `photoKeys` has been on the review body beside `rating`
 * and `comment` the whole time. The app collected photographs into local state
 * and stopped there - a note on the composer even said they "have nowhere to
 * upload to yet". They did. So every picture a customer attached to a review
 * lived on their handset and was lost on the next reinstall, and the review
 * that reached the platform had no evidence attached to it at all.
 *
 * Two steps, same shape as the profile picture: ask for a presigned PUT, send
 * the bytes straight to S3, submit the key. The bytes never pass through the
 * API, so a photo on a bad line cannot hold a request open.
 *
 * Resolves to `null` rather than throwing on any failure. A review is worth
 * more than its photographs: a customer who wrote five stars and a sentence
 * should not lose both because one upload timed out, so the caller submits
 * whatever keys it got and the rest are simply not there.
 */
export async function uploadReviewPhoto(
  bookingId: string,
  uri: string,
  /* The picker downscales everything it returns to JPEG - see
     `services/photos.ts`. Passed rather than assumed so a future picker that
     keeps PNG does not silently mislabel every upload. */
  contentType = 'image/jpeg',
): Promise<string | null> {
  try {
    const ticket = await api.post<ReviewPhotoTicket>(
      `/bookings/${bookingId}/review/photos/upload-url`,
      { contentType },
    );

    /*
     * `fetch`, not the app's client: this goes to S3 and must carry no
     * Authorization header. The presigned URL is the credential, and a second
     * one makes S3 refuse the request outright.
     */
    const file = await fetch(uri);
    const body = await file.blob();

    const put = await fetch(ticket.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body,
    });
    if (!put.ok) {
      return null;
    }

    return ticket.photoKey;
  } catch {
    return null;
  }
}

/**
 * Uploads a whole strip, keeping only what landed.
 *
 * Sequential rather than parallel, deliberately: this runs on a handset that
 * has just finished a booking flow, five photographs is the cap, and five
 * simultaneous PUTs on a weak connection is how all five fail together.
 *
 * A photograph that already carries a remote key is passed through untouched -
 * editing a review must not re-upload what is already on the platform.
 */
export async function uploadReviewPhotos(
  bookingId: string,
  photos: Array<{ uri: string; key?: string }>,
): Promise<string[]> {
  const keys: string[] = [];

  for (const photo of photos) {
    if (photo.key) {
      keys.push(photo.key);
      continue;
    }
    const key = await uploadReviewPhoto(bookingId, photo.uri);
    if (key) {
      keys.push(key);
    }
  }

  return keys;
}
